import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  BarChart3, 
  Users, 
  DollarSign, 
  Package, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  ChevronDown,
  Globe,
  Shield,
  AlertCircle
} from "lucide-react";

const SubscriptionMonitoringDashboard = ({ API, token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchMonitoringData();
  }, []);

  const fetchMonitoringData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/admin/subscription-monitoring`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching monitoring data:", error);
      toast.error("Failed to fetch subscription monitoring data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-20">
          <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500">Failed to load monitoring data</p>
          <Button onClick={() => fetchMonitoringData()} variant="outline" className="mt-4">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-blue-600" />
            Subscription Monitoring
          </h2>
          <p className="text-gray-500 mt-1">
            Real-time overview of all subscription plans and users
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data.subscription_management_enabled ? (
            <Badge className="bg-green-100 text-green-700 text-sm py-1">
              <Shield className="h-3 w-3 mr-1" />
              Subscriptions Active
            </Badge>
          ) : (
            <Badge className="bg-orange-100 text-orange-700 text-sm py-1">
              <AlertCircle className="h-3 w-3 mr-1" />
              Subscriptions Disabled
            </Badge>
          )}
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => fetchMonitoringData(true)}
            disabled={refreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-2 border-blue-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Plans</p>
                <p className="text-3xl font-bold text-blue-600">{data.total_plans}</p>
              </div>
              <Package className="h-10 w-10 text-blue-200" />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {data.active_plans} active
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Subscriptions</p>
                <p className="text-3xl font-bold text-green-600">{data.total_active_subscriptions}</p>
              </div>
              <Users className="h-10 w-10 text-green-200" />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              {data.total_expired_subscriptions} expired
            </p>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Revenue</p>
                <div className="text-2xl font-bold text-purple-600">
                  {data.revenue_by_currency && data.revenue_by_currency.length > 0 ? (
                    data.revenue_by_currency.map((rev, idx) => (
                      <div key={idx} className="flex items-center gap-1">
                        <span>{rev.formatted}</span>
                      </div>
                    ))
                  ) : (
                    <span>$0</span>
                  )}
                </div>
              </div>
              <DollarSign className="h-10 w-10 text-purple-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-100">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Conversion Rate</p>
                <p className="text-3xl font-bold text-orange-600">
                  {data.total_active_subscriptions > 0 
                    ? `${Math.round((data.total_active_subscriptions / (data.total_active_subscriptions + data.total_expired_subscriptions)) * 100)}%`
                    : '0%'
                  }
                </p>
              </div>
              <TrendingUp className="h-10 w-10 text-orange-200" />
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Active vs Total
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Plans with Subscribers */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            Plans & Subscribers
          </CardTitle>
          <CardDescription>
            View all subscription plans with their subscribed users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.plans && data.plans.length > 0 ? (
            <Accordion type="multiple" className="space-y-2">
              {data.plans.map((plan, index) => (
                <AccordionItem 
                  key={plan.plan_id || index} 
                  value={plan.plan_id || `plan-${index}`}
                  className="border rounded-lg px-4"
                >
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${plan.is_active ? 'bg-green-500' : 'bg-gray-400'}`} />
                        <span className="font-semibold">{plan.plan_name}</span>
                        {plan.is_featured && (
                          <Badge className="bg-yellow-100 text-yellow-700">Featured</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div className="text-right">
                          <span className="font-medium">{plan.formatted_price}</span>
                          <span className="text-gray-400">/{plan.duration_type}</span>
                        </div>
                        <Badge variant="outline" className="bg-blue-50">
                          <Users className="h-3 w-3 mr-1" />
                          {plan.active_subscriptions} active
                        </Badge>
                        <Badge variant="outline" className="bg-green-50 text-green-700">
                          {plan.formatted_revenue} revenue
                        </Badge>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pt-2 pb-4">
                    <div className="space-y-4">
                      {/* Plan Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <Badge className={plan.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Currency</p>
                          <p className="font-medium">{plan.plan_currency}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Active Subs</p>
                          <p className="font-medium text-green-600">{plan.active_subscriptions}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Expired Subs</p>
                          <p className="font-medium text-gray-500">{plan.expired_subscriptions}</p>
                        </div>
                      </div>

                      {/* Accessible Modules */}
                      <div>
                        <p className="text-sm font-medium mb-2">Accessible Modules ({plan.accessible_modules?.length || 0})</p>
                        <div className="flex flex-wrap gap-1">
                          {plan.accessible_modules && plan.accessible_modules.length > 0 ? (
                            plan.accessible_modules.slice(0, 10).map((module, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {module}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-gray-400 text-sm">No modules assigned</span>
                          )}
                          {plan.accessible_modules && plan.accessible_modules.length > 10 && (
                            <Badge variant="outline" className="text-xs bg-gray-100">
                              +{plan.accessible_modules.length - 10} more
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Subscribed Users Table */}
                      <div>
                        <p className="text-sm font-medium mb-2">Subscribed Users</p>
                        {plan.users && plan.users.length > 0 ? (
                          <div className="border rounded-lg overflow-hidden">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50">
                                  <TableHead>Name</TableHead>
                                  <TableHead>Email</TableHead>
                                  <TableHead>Country</TableHead>
                                  <TableHead>Subscribed</TableHead>
                                  <TableHead>Expires</TableHead>
                                  <TableHead className="text-right">Amount</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {plan.users.map((user, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{user.name}</TableCell>
                                    <TableCell className="text-gray-500">{user.email}</TableCell>
                                    <TableCell>
                                      <Badge variant="outline" className="text-xs">
                                        <Globe className="h-3 w-3 mr-1" />
                                        {user.country || 'N/A'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-500">
                                      {user.subscribed_at ? new Date(user.subscribed_at).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-500">
                                      {user.expires_at ? new Date(user.expires_at).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell className="text-right font-medium">
                                      {user.payment_currency} {user.payment_amount || 0}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                            <p className="text-gray-400">No active subscribers for this plan</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          ) : (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No subscription plans created yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Create plans in "Subscription Plans" tab to see them here
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionMonitoringDashboard;
